import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  InputAdornment,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  TextField,
  Badge,
  Skeleton,
  Paper,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import ChatBox from './ChatBox';
import ChatListItem from './ChatListItem';
import { useSocket } from '../../socket/SocketContext';
import AddMenuFab from './AddMenuFab';
import ChatCreation from './ChatCreation';
import GroupCreation from './GroupCreation';
import chatApi from './ChatApi';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { closeChat, openChat, setRefreashNeeded, setAiHistory } from '../../redux/features/chatSlice';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { Snackbar, Alert, Tooltip } from '@mui/material';
import { addTotalChatCount } from '../../redux/features/socketNotificationSlice';
export default function ChatDrawer() {
  const dispatch = useDispatch();
  const socket = useSocket();
  const chat = useSelector((state) => state.chat);
  const { totalChatCount } = useSelector((state) => state.socketNotification);
  const { open, chatRoomId, groupCreationUsersList, groupName, rowDetails, isAiMode, aiHistory } = chat;
  const drawerOpen = open;

  const [activeTab, setActiveTab] = useState(0); // 0: All, 1: Personal, 2: Groups
  const [searchTerm, setSearchTerm] = useState('');
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [chats, setChats] = useState([]); //ALL CHATS OF USER
  const [archivedChats, setArchivedChats] = useState([]); //CHATS THE USER ARCHIVED - HIDDEN FROM THE LIST ABOVE
  const [showArchived, setShowArchived] = useState(false); //ARE WE LOOKING AT THE ARCHIVED SECTION?
  const [activeChatRoomId, setActiveChatRoomId] = useState(null); //ACTIVE CHAT ROOM ID BY CLICK WHICH CHAT ENTER
  const [contentBox, SetContentBox] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [groupCreating, setGroupCreating] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const showAlert = (message, severity = 'success') => {
    setAlert({ open: true, message, severity });
  };

  const typingTimeoutRef = useRef(null);
  const activeChatRef = useRef(null);
  // socket events arrive outside of render, so they need a plain ref to know
  // whether an incoming update belongs to the archived section or the main list
  const archivedIdsRef = useRef(new Set());

  useEffect(() => {
    archivedIdsRef.current = new Set(archivedChats.map((c) => String(c.chat_room_id)));
  }, [archivedChats]);

  const loginedUserId = React.useMemo(() => {
    try {
      const cred = localStorage.getItem('cred');
      return cred ? JSON.parse(cred)?.profile?.userId : null;
    } catch {
      return null;
    }
  }, []);

  //when activechatroom id set we need to get the object of that chat means active chat
  const activeChat = React.useMemo(() => {
    if (isAiMode) {
        return {
            type: 'AI',
            group_name: 'AI Assistant',
            isAi: true
        };
    }
    if (!activeChatRoomId) return null;
    return [...chats, ...archivedChats].find((c) => c.chat_room_id == activeChatRoomId) || null;
  }, [activeChatRoomId, chats, archivedChats, isAiMode]);

  useEffect(() => {
    activeChatRef.current = activeChat;
  }, [activeChat]);

  useEffect(() => {
    if(drawerOpen){
      dispatch(
        addTotalChatCount([...chats, ...archivedChats].reduce((acc, data) => acc + (data.unread_count || 0), 0))
      )
    }
  }, [chats,archivedChats,drawerOpen]);

  // useEffect(() => {
  //   if (!drawerOpen) return;
  //   if (chatRoomId) {
  //     console.log('chatRoomId exist so direct selectChat', chatRoomId);
  //     selectChat(chatRoomId); //its a functin make it as activeChatID and then also activeChat with room id and also getting message
  //   } else if (groupCreationUsersList.length > 0) {
  //     console.log('groupCreationUsersList exist so direct createNewGroup', groupCreationUsersList);
  //     createNewGroup(groupName || generateGroupName(), groupCreationUsersList);
  //   }
  // }, [open, chatRoomId, groupCreationUsersList]);

  /* -------------------- 🔑 SINGLE INITIALIZER -------------------- */
  useEffect(() => {
    if (!drawerOpen) return;

    const init = async () => {
      const chatList = await getChatList();

      // CASE 1: Redux chatRoomId → open specific chat
      if (chatRoomId) {
        // compare loosely: chat_room_id can arrive as a number from the chat API and
        // as a string from the module that opened the drawer (refund, notification…)
        const exists = chatList.some((c) => String(c.chat_room_id) === String(chatRoomId));

        // Refund rooms: let the server decide - it reuses the existing room and admits
        // the caller only if they are a member or pass its Finance check (403 otherwise).
        if (!exists && rowDetails?.expense_id) {
          await createNewGroup(groupName || generateGroupName(), groupCreationUsersList);
          return;
        }

        if (!exists) {
          // console.log('You are not part of this chat. Please ask the admin to add you.');
          // enqueueSnackbar('You are not part of this chat. Please ask the admin to add you.', {
          //   variant: 'warning',
          //   anchorOrigin: {
          //     vertical: 'bottom',
          //     horizontal: 'right',
          //   },
          //   style: {
          //     fontSize: '0.75rem',
          //     padding: '6px 10px',
          //     minWidth: 'auto',
          //     borderRadius: '6px',
          //   },
          // });
          showAlert('Access denied. Contact Group admin.', 'warning');

          return;
        }

        await selectChat(chatRoomId);
        return;
      }

      // CASE 2: Group creation
      if (!chatRoomId && groupCreationUsersList.length > 0) {
        await createNewGroup(groupName || generateGroupName(), groupCreationUsersList);
      }
    };

    init();
  }, [drawerOpen, chatRoomId, groupCreationUsersList]);

  const handleClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setChats([]);
    setArchivedChats([]);
    setShowArchived(false);
    setActiveChatRoomId(null);
    SetContentBox(null);
    setMessages([]);
    dispatch(closeChat());
  };

  // useEffect(() => {
  //   if (open) {
  //     console.log('CHAT STATE →', {
  //       reduxChatRoomId: chatRoomId,
  //       reduxGroupCreationUsersList: groupCreationUsersList,
  //       reduxGroupName: groupName,
  //       reduxRowDetails: rowDetails,
  //       activeChatRoomId,
  //       activeChat,
  //       chats,
  //       messages,
  //       drawerOpen,
  //     });
  //   }
  // }, [activeChatRoomId, activeChat, chats, messages, drawerOpen]);

  const generateGroupName = () => {
    const now = new Date();
    const date = now.toLocaleDateString('en-GB'); // 12/01/2026
    const time = now.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    }); // 14:32

    return `Group ${date} ${time}`;
  };

   const getChatList = async () => {
    try {
      setListLoading(true);
      // the archived rooms are pulled separately so they never leak into the
      // normal list, but callers still get every room they are a member of
      const [data, archived] = await Promise.all([
        chatApi.getChatListApiTrigger({ archived: false }),
        chatApi.getChatListApiTrigger({ archived: true }),
      ]);
      setChats(data);
      setArchivedChats(archived);
      return [...data, ...archived];
    } catch (err) {
      console.error('Failed to load chat list', err);
      setChats([]);
      setArchivedChats([]);
      return [];
    } finally {
      setListLoading(false);
    }
  };

  /**
   * Archive / unarchive a conversation for the logged in user only. The chat is
   * never deleted - it just moves between the main list and the archived
   * section, and can be moved back at any time.
   */
  const handleToggleArchive = async (roomId, archive) => {
    const source = archive ? chats : archivedChats;
    const target = source.find((c) => String(c.chat_room_id) === String(roomId));
    if (!target) return;

    // optimistic move so the list reacts instantly
    if (archive) {
      setChats((prev) => prev.filter((c) => String(c.chat_room_id) !== String(roomId)));
      setArchivedChats((prev) => [target, ...prev]);
      if (String(activeChatRoomId) === String(roomId)) {
        setActiveChatRoomId(null);
        setMessages([]);
      }
    } else {
      setArchivedChats((prev) => prev.filter((c) => String(c.chat_room_id) !== String(roomId)));
      setChats((prev) => [target, ...prev]);
      if (archivedChats.length <= 1) setShowArchived(false);
    }

    try {
      await chatApi.setChatArchived(roomId, archive);
      showAlert(archive ? 'Chat archived' : 'Chat moved back to your chats', 'success');
    } catch (error) {
      console.error('❌ Error updating archive status:', error);
      showAlert('Could not update the archive status', 'error');
      getChatList(); // roll back to whatever the server actually has
    }
  };

  const selectChat = async (chatRoomId) => {
    try {
      setMessagesLoading(true);
      setMessages([]); // reset FIRST

      const res = await chatApi.getMessages(chatRoomId, { page: 1, limit: 10, search: '' });
      const normalizedMessages = Array.isArray(res) ? [...res].reverse() : [];

      setActiveChatRoomId(chatRoomId);
      setPage(1);
      setHasMore(true);
      setMessages(normalizedMessages);
      if (res.length === 0) {
        setHasMore(false);
      }
      if (socket) {
        socket.emit('mark_read', { chat_room_id: chatRoomId });
      }
      const clearUnread = (prev) => prev.map((c) => (c.chat_room_id === chatRoomId ? { ...c, unread_count: 0 } : c));
      setChats(clearUnread);
      setArchivedChats(clearUnread);
    } catch (error) {
      console.error('❌ Error loading messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  const loadMoreMessages = async () => {
    if (isAiMode) return;
    if (!hasMore || loadingMore || !activeChatRoomId) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;

      const res = await chatApi.getMessages(activeChatRoomId, {
        page: nextPage,
      });

      if (res.length === 0) {
        setHasMore(false);
        return;
      }
      const normalizedMessages = [...res].reverse();
      setMessages((prev) => [...normalizedMessages, ...prev]);
      setPage(nextPage);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleTyping = () => {
    if (isAiMode) return;
    if (!socket || !activeChat) return;

    const isGroup = activeChat.type === 'GROUP';
    const otherUserId = isGroup ? undefined : activeChat.participants[0].userId;

    socket.emit('user_typing', {
      chat_room_id: activeChat.chat_room_id,
      toUserId: otherUserId,
    });

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('user_stopped_typing', {
        chat_room_id: activeChat.chat_room_id,
        toUserId: otherUserId,
      });
    }, 2000);
  };

  const lastAiContextRef = useRef(null);

  const sendMessage = async (text) => {
    if (isAiMode) {
        const userMsg = {
            message_id: Date.now(),
            message: text.trim(),
            sender: loginedUserId,
            created_at: new Date().toISOString(),
            message_type: 'TEXT'
        };
        const updatedHistoryWithUser = [...aiHistory, userMsg];
        dispatch(setAiHistory(updatedHistoryWithUser));
        setIsTyping(true);

        try {
            const res = await chatApi.askAi(text.trim(), lastAiContextRef.current);
            console.log('AI Response:', res);
            
            // Save the new context for the next message
            if (res.data?.intent || res.data?.entities) {
                lastAiContextRef.current = {
                    intent: res.data?.intent,
                    entities: res.data?.entities
                };
            }

            const aiMsg = {
                message_id: Date.now() + 1,
                message: res.data?.reply || res.reply || (typeof res.data === 'string' ? res.data : "I'm sorry, I couldn't process that."),
                sender: 'AI',
                created_at: new Date().toISOString(),
                message_type: 'TEXT'
            };
            dispatch(setAiHistory([...updatedHistoryWithUser, aiMsg]));

        } catch (error) {
            console.error('AI Error:', error);
        } finally {
            setIsTyping(false);
        }
        return;
    }

    if (!socket || !activeChat || !text.trim()) return;
    // console.log('text', text);
    const isGroup = activeChat.type === 'GROUP';
    const otherUserId = isGroup ? undefined : activeChat.participants[0].userId;

    socket.emit('send_message', {
      chat_room_id: activeChat.chat_room_id,
      toUserId: otherUserId,
      message: text.trim(),
    });

    socket.emit('user_stopped_typing', {
      chat_room_id: activeChat.chat_room_id,
      toUserId: otherUserId,
    });
  };

  const createNewGroup = async (groupName, memberIds) => {
    try {
      // console.log('creating new group with ', groupName, memberIds);

      setGroupCreating(true);
      setMessagesLoading(true);
      setListLoading(true);
      const res = await chatApi.createGroup(groupName, memberIds, rowDetails);
      const room = res.data;
      if (!room?.chat_room_id) return;
      const updatedChats = await getChatList();
      const exists = updatedChats.find((c) => c.chat_room_id === room.chat_room_id);
      if (exists) {
        await selectChat(room.chat_room_id);
      }
      dispatch(setRefreashNeeded(true));
    } catch (error) {
      console.error('❌ Error creating group:', error);
      if (error?.response?.status === 403) {
        showAlert('Access denied. Contact Group admin.', 'warning');
      }
      // alert('Failed to create group');
    } finally {
      setGroupCreating(false);
      setMessagesLoading(false);
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (!socket) return;
    // if (drawerOpen) {
    //   getChatList();
    // }

    // socket.on('new_message', (msg) => {
    //   const active = activeChatRef.current;
    //   if (msg.chat_room_id === active?.chat_room_id) {
    //     setMessages((prev) => {
    //       const exists = prev.find((m) => m.message_id === msg.message_id);
    //       if (exists) return prev;
    //       return [...prev, msg];
    //     });

    //     if (msg.sender !== loginedUserId) {
    //       socket.emit('mark_read', { chat_room_id: msg.chat_room_id });
    //     }
    //   }

    // });

    socket.on('new_message', (msg) => {
      // console.log('new message', msg);

      const active = activeChatRef.current;

      // CASE 1: Active chat → update messages
      if (msg.chat_room_id === active?.chat_room_id) {
        setMessages((prev) => {
          const exists = prev.find((m) => m.message_id === msg.message_id);
          if (exists) return prev;
          return [...prev, msg];
        });

        if (msg.sender !== loginedUserId) {
          socket.emit('mark_read', { chat_room_id: msg.chat_room_id });
        }
      }
      // CASE 2: Other chat → update chat list
      else {
        // an archived conversation stays archived when a new message lands,
        // it must not jump back into the visible list
        const applyUpdate = archivedIdsRef.current.has(String(msg.chat_room_id)) ? setArchivedChats : setChats;
        applyUpdate((prev) => {
          const index = prev.findIndex((c) => c.chat_room_id === msg.chat_room_id);

          // Existing chat → update it
          if (index !== -1) {
            const updated = [...prev];
            updated[index] = {
              ...updated[index],
              last_message: msg.message,
              last_message_time: msg.createdAt,
              unread_count: (updated[index].unread_count || 0) + 1,
            };

            // Move chat to top
            const [chat] = updated.splice(index, 1);
            return [chat, ...updated];
          }

          // Brand-new chat → add to top
          return [
            {
              chat_room_id: msg.chat_room_id,
              last_message: msg.message,
              last_message_time: msg.createdAt,
              unread_count: 1,
              type: msg.type,
              group_name: msg.group_name,
              participants: msg.participants,
            },
            ...prev,
          ];
        });
      }
    });

    socket.on('chat_list_update', (update) => {
      const active = activeChatRef.current;
      // console.log('📋 Chat list update:', update);
      const applyUpdate = archivedIdsRef.current.has(String(update.chat_room_id)) ? setArchivedChats : setChats;
      applyUpdate((prev) => {
        const index = prev.findIndex((c) => c.chat_room_id === update.chat_room_id);

        if (index !== -1) {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            last_message: update.last_message,
            last_message_time: update.last_message_time,
            last_message_sender: update.last_message_sender,
            unread_count:
              active?.chat_room_id === update.chat_room_id
                ? 0
                : (update.unread_count ?? updated[index].unread_count + 1),
          };

          const [movedChat] = updated.splice(index, 1);
          return [movedChat, ...updated];
        } else {
          //CANNOT FIND IN CURRENT CHAT LIST SO ADD NEW CHAT
          return [
            {
              chat_room_id: update.chat_room_id,
              type: update.type,
              group_name: update.group_name,
              group_photo: update.group_photo,
              other_user_id: update.other_user_id,
              other_user_name: update.other_user_name,
              other_user_photo: update.other_user_photo,
              last_message: update.last_message,
              last_message_time: update.last_message_time,
              last_message_sender: update.last_message_sender,
              member_count: update.member_count,
              unread_count: update.unread_count ?? 1,
            },
            ...prev,
          ];
        }
      });
    });

    socket.on('messages_read', ({ chat_room_id }) => {
      const active = activeChatRef.current;
      if (active?.chat_room_id === chat_room_id) {
        const clearUnread = (prev) => prev.map((c) => (c.chat_room_id === chat_room_id ? { ...c, unread_count: 0 } : c));
        setChats(clearUnread);
        setArchivedChats(clearUnread);
      }
    });

    socket.on('user_typing', ({ chat_room_id, typing, user_id }) => {
      const active = activeChatRef.current;
      if (active?.chat_room_id === chat_room_id) {
        if (user_id != loginedUserId) {
          setIsTyping(typing);
        }
      }
    });

    socket.on('group_members_added', ({ chat_room_id }) => {
      const active = activeChatRef.current;
      if (active && active?.chat_room_id === chat_room_id) {
        getChatList();
      }
    });

    socket.on('group_member_left', ({ chat_room_id }) => {
      const active = activeChatRef.current;
      if (active && active?.chat_room_id === chat_room_id) {
        getChatList();
      }
    });

    socket.on('error', (error) => {
      console.error('❌ Socket error:', error);
      showAlert(error.message, 'error');
    });

    return () => {
      socket.off('new_message');
      socket.off('chat_list_update');
      socket.off('messages_read');
      socket.off('user_typing');
      socket.off('group_members_added');
      socket.off('group_member_left');
    };
  }, [socket, drawerOpen, loginedUserId]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleBackToList = () => {
    if (isAiMode) {
        dispatch(openChat({ isAiMode: false }));
        return;
    }
    setActiveChatRoomId(null);
    SetContentBox(null);
  };

  const archivedUnreadCount = archivedChats.reduce((acc, c) => acc + (c.unread_count || 0), 0);

  const displayedList = (showArchived ? archivedChats : chats)
    .filter((chat) => {
      if (activeTab === 1) return chat.type === 'ONE_TO_ONE';
      if (activeTab === 2) return chat.type === 'GROUP';
      return true;
    })
    .filter((chat) => {
      const search = searchTerm.toLowerCase();

      const name = chat.type === 'GROUP' ? chat.group_name : chat.participants?.[0]?.name;

      return name?.toLowerCase().includes(search);
    })
    .map((chat) => (
      <ChatListItem
        key={chat.chat_room_id}
        chat={chat}
        onClick={selectChat}
        onArchiveToggle={handleToggleArchive}
        isArchived={showArchived}
      />
    ));

  return (
    <>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return;
          handleClose();
        }}
        disableRestoreFocus
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            m: { xs: 0, sm: 2 },
            height: {
              xs: '100%', // 👈 full height on mobile
              sm: 'calc(100% - 32px)', // 👈 margin-adjusted height on sm+
            },
            borderRadius: { xs: 0, sm: 2 },
            overflow: 'hidden',
            backgroundColor: '#551364',
          },
        }}
      >
        {groupCreating && drawerOpen && !activeChat && !chatRoomId ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              backgroundColor: '#f9ecff',
            }}
          >
            <Skeleton variant="circular" width={64} height={64} />
            <Typography fontWeight={700} fontSize="1rem">
              Creating group…
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait a moment
            </Typography>
          </Box>
        ) : activeChat ? (
          <ChatBox
            messages={isAiMode ? aiHistory : messages}
            chat={activeChat}
            onBack={handleBackToList}
            onClose={handleClose}
            loginedUserId={loginedUserId}
            onSend={sendMessage}
            onTyping={handleTyping}
            isTyping={isTyping}
            messagesLoading={messagesLoading}
            selectChat={selectChat}
            loadMoreMessages={loadMoreMessages}
            hasMore={isAiMode ? false : hasMore}
            loadingMore={loadingMore}
            getChatList={getChatList}
            handleBackToList={handleBackToList}
          />
        ) : (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                px: 2,
                py: 2,
                bgcolor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: '#551364',
                backgroundImage: `url("/static/illustrations/chatTop.png")`,
              }}
            >
              {/* Top row */}
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1.2}>
                  {/* Logo */}
                  <Box
                    component="img"
                    // src="/static/illustrations/Genie.png"
                    src="https://student.intervaledu.com/_next/image?url=https%3A%2F%2Fportal.teaminterval.net%2Fstatic%2Fmedia%2Fmap.7dd1ec7c87cddefd09e4.gif&w=128&q=75"
                    alt="Genie"
                    sx={{ height: 30, width: 30, transform: 'scale(1.25)' }}
                  />

                  {/* App Name */}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 700,
                        lineHeight: 1.1,
                        letterSpacing: 0.3,
                        display: 'flex',
                        alignItems: 'baseline', // 👈 key fix
                      }}
                    >
                      {/* Big I */}
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 20,
                          fontWeight: 700,
                          lineHeight: 1,
                          color: 'white',
                          letterSpacing: 0.3,
                        }}
                      >
                        i
                      </Typography>

                      {/* n */}
                      <Typography component="span" sx={{ color: 'white', fontWeight: 600, letterSpacing: 0.3 }}>
                        n
                      </Typography>

                      {/* te */}
                      <Typography component="span" sx={{ color: 'white', fontWeight: 600, letterSpacing: 0.3 }}>
                        te
                      </Typography>

                      {/* Chat */}
                      <Typography component="span" sx={{ color: 'white', ml: 0.5, fontWeight: 600 }}>
                        Chat
                      </Typography>
                    </Typography>
                    {/* <Typography
                        sx={{
                          fontSize: 11,
                          color: 'rgba(255,255,255,0.75)',
                        }}
                      >
                        Team Conversations
                      </Typography> */}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Tooltip title="Ask Smart HR Assistant">
                        <IconButton 
                            onClick={() => dispatch(openChat({ isAiMode: true }))}
                            sx={{ 
                                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', // Golden Gradient
                                color: '#561f6e',
                                width: 40,
                                height: 40,
                                boxShadow: '0 0 15px rgba(255, 215, 0, 0.6)',
                                animation: 'pulseAi 2s infinite',
                                '&:hover': { 
                                    background: 'linear-gradient(135deg, #FFA500 0%, #FFD700 100%)',
                                    transform: 'scale(1.1)',
                                    boxShadow: '0 0 25px rgba(255, 215, 0, 0.8)',
                                },
                                transition: 'all 0.3s ease',
                                '@keyframes pulseAi': {
                                    '0%': { boxShadow: '0 0 0 0 rgba(255, 215, 0, 0.7)' },
                                    '70%': { boxShadow: '0 0 0 10px rgba(255, 215, 0, 0)' },
                                    '100%': { boxShadow: '0 0 0 0 rgba(255, 215, 0, 0)' },
                                }
                            }}
                        >
                            <SmartToyIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={handleClose} sx={{ color: '#ffffff' }}>
                        <HighlightOffIcon sx={{ color: '#fff', fontSize: 20 }} />
                    </IconButton>
                </Box>
              </Box>

              {/* Secondary content */}
              {contentBox === null && (
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search people or groups"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon icon="mdi:magnify" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mt: 1,
                    bgcolor: 'grey.100',
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                />
              )}
            </Box>

            {/* Tabs */}
            {contentBox == 'createChat' ? (
              <ChatCreation selectChat={selectChat} getChatList={getChatList} setListLoading={setListLoading} />
            ) : contentBox == 'creatGroup' ? (
              <GroupCreation selectChat={selectChat} setListLoading={setListLoading} getChatList={getChatList} />
            ) : (
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }}
              >
                {/* TAB SECTION START */}
                <Box px={1.5} py={1.5} sx={{ bgcolor: '#f9ecff', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                  <Box
                    sx={{
                      bgcolor: '#ffffff',
                      // p: 1.5,
                      borderRadius: '999px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    }}
                  >
                    <Tabs
                      value={activeTab}
                      onChange={handleTabChange}
                      variant="fullWidth"
                      TabIndicatorProps={{ style: { display: 'none' } }}
                      sx={{
                        minHeight: 'unset',
                        '& .MuiTabs-flexContainer': {
                          gap: 1,
                          p: 1.5,
                        },
                      }}
                    >
                      {['All', 'Personal', 'Groups'].map((label) => (
                        <Tab
                          key={label}
                          label={label}
                          sx={{
                            border: '1px solid #620a9954',
                            minHeight: 'unset',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            borderRadius: '999px',
                            px: 3,
                            py: 1.2,

                            bgcolor: 'rgba(86, 31, 110, 0.08)',
                            color: '#561f6e',

                            transition: 'all 0.25s ease',

                            '&:hover': {
                              bgcolor: 'rgba(86, 31, 110, 0.14)',
                            },

                            '&.Mui-selected': {
                              bgcolor: '  #561f6e',
                              color: '#ffffff',
                              transform: 'scale(1.06)',
                            },
                          }}
                        />
                      ))}
                    </Tabs>
                  </Box>
                </Box>

                {/* TAB SECTION END  */}

                {/* ARCHIVED SECTION - entry row on the normal list, header while inside it */}
                {showArchived || archivedChats.length > 0 ? (
                  <Box
                    onClick={() => setShowArchived(!showArchived)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1.2,
                      cursor: 'pointer',
                      bgcolor: showArchived ? '#f0dcf7' : '#f9ecff',
                      borderBottom: '1px solid rgba(0,0,0,0.08)',
                      transition: 'background-color 0.18s ease',
                      '&:hover': { bgcolor: '#f0dcf7' },
                    }}
                  >
                    <Icon
                      icon={showArchived ? 'mdi:arrow-left' : 'mdi:archive-outline'}
                      width={20}
                      height={20}
                      color="#561f6e"
                    />
                    <Typography fontWeight={600} fontSize="0.9rem" color="#561f6e" sx={{ flexGrow: 1 }}>
                      Archived
                    </Typography>

                    {archivedUnreadCount > 0 && (
                      <Box
                        sx={{
                          minWidth: 18,
                          height: 18,
                          px: 0.7,
                          borderRadius: '999px',
                          bgcolor: '#0089d0',
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {archivedUnreadCount}
                      </Box>
                    )}

                    <Typography variant="caption" color="text.secondary">
                      {archivedChats.length}
                    </Typography>
                  </Box>
                ) : null}

                {/* List Content */}
                <List
                  sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    px: 1,
                    py: 1,
                    backgroundColor: '#f9ecff',
                  }}
                >
                  {listLoading ? (
                    [1, 2, 3, 4, 5, 6].map((i) => (
                      <Paper key={i} sx={{ mb: 1 }}>
                        <ListItem key={i}>
                          <ListItemAvatar>
                            <Skeleton variant="circular" width={40} height={40} />
                          </ListItemAvatar>
                          <ListItemText
                            primaryTypographyProps={{ component: 'div' }}
                            primary={<Skeleton width="100%" height={60} />}
                          />
                        </ListItem>
                      </Paper>
                    ))
                  ) : displayedList.length > 0 ? (
                    displayedList
                  ) : (
                    <Box
                      sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        px: 3,
                        py: 6,
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          bgcolor: 'rgba(86,31,110,0.08)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 1,
                        }}
                      >
                        <Icon icon="mdi:chat-outline" width={38} height={38} color="#561f6e" />
                      </Box>

                      {/* Title */}
                      <Typography fontWeight={700} fontSize="1rem" color="#0c3e6b">
                        No conversations yet
                      </Typography>

                      {/* Subtitle */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          maxWidth: 220,
                        }}
                      >
                        Start a chat or create a group to begin the conversation.
                      </Typography>
                    </Box>
                  )}
                </List>
              </Box>
            )}
          </Box>
        )}

        {activeChat === null && (
          <AddMenuFab SetContentBox={SetContentBox} contentBox={contentBox} hasChats={chats.length > 0} />
        )}
      </Drawer>

      {/* {!drawerOpen && (
        <IconButton
          onClick={() => dispatch(openChat({}))}
          sx={{
            position: 'fixed',
            bottom: { xs: 12, sm: 24 },
            right: { xs: 12, sm: 24 },
            zIndex: 1199,
            width: { xs: 46, sm: 58 },
            height: { xs: 46, sm: 58 },
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #561f6e, #0c3e6b)',
            color: '#fff',
            boxShadow: {
              xs: '0 6px 18px rgba(12,62,107,0.35)',
              sm: '0 10px 30px rgba(12,62,107,0.45)',
            },
            animation: { xs: 'none', sm: 'pulseGlow 2.5s infinite' },
            '&:hover': {
              transform: { sm: 'scale(1.08)' },
              boxShadow: { sm: '0 14px 40px rgba(12,62,107,0.6)' },
            },
            transition: 'all 0.3s ease',
            '@keyframes pulseGlow': {
              '0%': {
                boxShadow: '0 0 0 0 rgba(86,31,110,0.55)',
              },
              '70%': {
                boxShadow: '0 0 0 16px rgba(86,31,110,0)',
              },
              '100%': {
                boxShadow: '0 0 0 0 rgba(86,31,110,0)',
              },
            },
          }}
        >
          <Icon
            icon="mdi:chat"
            width={26}
            height={26}
            style={{
              animation: 'iconBounce 2.5s infinite',
            }}
          />

          <style>
            {`
          @keyframes iconBounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-3px); }
          }
        `}
          </style>
        </IconButton>
      )} */}

      {!drawerOpen && (
        <Badge
          badgeContent={totalChatCount}
          color="error"
          max={99}
          invisible={!totalChatCount}
          sx={{
            position: 'fixed',
            bottom: { xs: 12, sm: 24 },
            right: { xs: 12, sm: 24 },
            zIndex: 1200,
            '& .MuiBadge-badge': {
              fontSize: '0.7rem',
              fontWeight: 700,
              minWidth: 18,
              height: 18,
              borderRadius: '50%',
            },
          }}
        >
          <IconButton
            onClick={() => dispatch(openChat({}))}
            sx={{
              width: { xs: 46, sm: 58 },
              height: { xs: 46, sm: 58 },
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #561f6e, #0c3e6b)',
              color: '#fff',
              boxShadow: {
                xs: '0 6px 18px rgba(12,62,107,0.35)',
                sm: '0 10px 30px rgba(12,62,107,0.45)',
              },
              '&:hover': {
                transform: { sm: 'scale(1.08)' },
                boxShadow: { sm: '0 14px 40px rgba(12,62,107,0.6)' },
              },
            }}
          >
            <Icon icon="mdi:chat" width={26} height={26} />
          </IconButton>
        </Badge>
      )}

      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={alert.severity} onClose={() => setAlert({ ...alert, open: false })} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </>
  );
}
